/**
 * PreShiftBriefingCard Component (Phase 21.2.5)
 *
 * Green gradient hero card shown at the top of every operator portal tab.
 * Surfaces zone-relevant SOPs, recent assessment highlights (NO raw scores),
 * and suggested training content.
 *
 * - Dismissible via sessionStorage (comes back next session)
 * - Self-manages visibility (loading, empty, dismissed states)
 * - No props needed from parent layout
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList,
  BarChart3,
  Lightbulb,
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useOperatorBriefing } from '../../../hooks/useOperatorPortal';
import type { BriefingAssessment } from '../../../hooks/useOperatorPortal';
import { formatRelativeDate } from '../utils';

const DISMISS_KEY = 'op_briefingDismissed';

/** Read dismiss state from sessionStorage (safe for private browsing) */
function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Write dismiss state to sessionStorage (safe for private browsing) */
function setDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, 'true');
  } catch {
    // sessionStorage unavailable -- card will re-appear, but never crashes
  }
}

/** Format seconds to "X min" */
function formatDuration(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  return mins < 1 ? '1 min' : `${mins} min`;
}

/** Trend icon + label mapping */
function TrendIndicator({ trend }: { trend: BriefingAssessment['trend'] }) {
  switch (trend) {
    case 'improving':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-200">
          <TrendingUp size={14} />
          Improving
        </span>
      );
    case 'declining':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-200">
          <TrendingDown size={14} />
          Needs focus
        </span>
      );
    case 'steady':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-white/70">
          <Minus size={14} />
          Steady
        </span>
      );
    case 'first':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-200">
          <Star size={14} />
          First assessment
        </span>
      );
  }
}

export function PreShiftBriefingCard() {
  const { data, isLoading } = useOperatorBriefing();
  const [dismissed, setDismissedState] = useState(isDismissed);

  // If dismissed, render nothing
  if (dismissed) return null;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="mb-4 h-48 rounded-2xl bg-guild-green/20 animate-pulse" />
    );
  }

  // No data -- hide entirely (same as no operator linked)
  if (!data) return null;

  const { focusSops, recentAssessments, suggestedContent } = data;
  const isEmpty = focusSops.length === 0 && recentAssessments.length === 0 && suggestedContent.length === 0;

  const handleDismiss = () => {
    setDismissed();
    setDismissedState(true);
  };

  return (
    <div
      role="region"
      aria-label="Pre-shift briefing"
      className={cn(
        'relative mb-4 rounded-2xl bg-gradient-to-br from-guild-green to-emerald-700 p-4 text-white',
        'shadow-lg'
      )}
      style={{ animation: 'slideDown 0.4s ease-out' }}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">Today&apos;s Briefing</h2>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss briefing"
          className="rounded-full p-1 transition-colors hover:bg-white/20 active:bg-white/30"
        >
          <X size={18} />
        </button>
      </div>

      {/* Empty state */}
      {isEmpty && (
        <p className="text-sm text-white/80">
          No briefing today. Check your goals and content below.
        </p>
      )}

      {/* Sections */}
      <div className="flex flex-col gap-3">
        {/* Focus SOPs */}
        {focusSops.length > 0 && (
          <div className="rounded-xl bg-white/15 backdrop-blur-sm p-3">
            <div className="mb-2 flex items-center gap-2">
              <ClipboardList size={16} className="flex-shrink-0" />
              <h3 className="text-sm font-semibold">Focus Areas</h3>
            </div>
            <ul className="flex flex-wrap gap-2">
              {focusSops.map((sop) => (
                <li
                  key={sop.id}
                  className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium"
                >
                  {sop.title}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recent Assessments */}
        {recentAssessments.length > 0 && (
          <div className="rounded-xl bg-white/15 backdrop-blur-sm p-3">
            <div className="mb-2 flex items-center gap-2">
              <BarChart3 size={16} className="flex-shrink-0" />
              <h3 className="text-sm font-semibold">Recent Assessments</h3>
            </div>
            <div className="flex flex-col gap-2">
              {recentAssessments.map((assessment) => (
                <div key={assessment.id} className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">
                      {assessment.sopTitle}
                      {assessment.taskFocus && (
                        <span className="text-white/70"> &middot; {assessment.taskFocus}</span>
                      )}
                    </span>
                    <TrendIndicator trend={assessment.trend} />
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-white/60">
                    <span>{formatRelativeDate(assessment.date)}</span>
                    {assessment.zoneName && (
                      <>
                        <span>&middot;</span>
                        <span>{assessment.zoneName}</span>
                      </>
                    )}
                  </div>
                  {assessment.aiSummary && (
                    <p className="mt-0.5 text-xs leading-relaxed text-white/80 line-clamp-2">
                      {assessment.aiSummary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Content */}
        {suggestedContent.length > 0 && (
          <div className="rounded-xl bg-white/15 backdrop-blur-sm p-3">
            <div className="mb-2 flex items-center gap-2">
              <Lightbulb size={16} className="flex-shrink-0" />
              <h3 className="text-sm font-semibold">Suggested Content</h3>
            </div>
            <div className="flex flex-col gap-2">
              {suggestedContent.map((item) => (
                <Link
                  key={item.assignmentId}
                  to={`/operator/content/${item.assignmentId}`}
                  className="flex items-center justify-between rounded-lg bg-white/10 px-3 py-2 transition-colors hover:bg-white/20 active:bg-white/25"
                >
                  <span className="text-xs font-medium">{item.title}</span>
                  <span className="flex-shrink-0 text-[11px] text-white/60">
                    {item.materialType === 'video' ? 'Video' : 'Doc'}
                    {formatDuration(item.duration) && ` \u00B7 ${formatDuration(item.duration)}`}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
